import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  PaginationState,
  OnChangeFn,
  Column,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, X, ChevronDown, Loader2 } from "lucide-react";
import { Category } from "@/app/types/category";
import { PaginatedResponse } from "@/app/types/api";
import { filterableColumns } from "./columns";

interface DataTableFacetedFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
}

// Faceted Filter component for table columns
const DataTableFacetedFilter: React.FC<DataTableFacetedFilterProps<Category, unknown>> = ({
  column,
  title,
  options,
}) => {
  const facets = column?.getFacetedUniqueValues();
  const selectedValues = new Set(column?.getFilterValue() as string[]);

  return (
    <div className="space-y-2">
      {title && <div className="text-sm text-muted-foreground">{title}</div>}
      <div className="flex flex-wrap gap-1">
        {options.map((option) => {
          const isSelected = selectedValues.has(option.value);
          const count = facets?.get(option.value) || 0;

          return (
            <Button
              key={option.value}
              variant={isSelected ? "default" : "outline"}
              size="sm"
              className="h-8 rounded-full"
              onClick={() => {
                if (isSelected) {
                  selectedValues.delete(option.value);
                } else {
                  selectedValues.add(option.value);
                }
                column?.setFilterValue(
                  selectedValues.size ? Array.from(selectedValues) : undefined
                );
              }}
            >
              {option.icon && (
                <option.icon className="mr-1 h-4 w-4 text-muted-foreground" />
              )}
              {option.label}
              {count ? ` (${count})` : ""}
              {isSelected && <X className="ml-1 h-4 w-4" />}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

interface CategoryDataTableProps {
  data: PaginatedResponse<Category>;
  columns: ColumnDef<Category>[];
  isLoading: boolean;
  allCategories: Category[]; // Needed for filter options
  pagination: PaginationState;
  pageCount: number;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onPaginationChange: OnChangeFn<PaginationState>;
  onSortingChange: OnChangeFn<SortingState>;
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
}

const CategoryDataTable: React.FC<CategoryDataTableProps> = ({
  data,
  columns,
  isLoading,
  allCategories,
  pagination,
  pageCount,
  sorting,
  columnFilters,
  searchValue,
  onSearchChange,
  onPaginationChange,
  onSortingChange,
  onColumnFiltersChange,
}) => {
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  
  // Dynamically generate filterable columns based on allCategories
  const dynamicFilterableColumns = filterableColumns(allCategories);

  const table = useReactTable({
    data: data.items,
    columns,
    pageCount,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination,
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange,
    onSortingChange,
    onColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-1 items-center space-x-2">
          <div className="relative w-full lg:w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search categories..."
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-8 bg-card"
            />
            {searchValue && (
              <Button
                variant="ghost"
                onClick={() => onSearchChange("")}
                className="absolute right-0 top-0 h-full px-3 py-2"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear</span>
              </Button>
            )}
          </div>
        </div>
        
        <div className="flex space-x-2">
          {/* Filters Dropdown */}
          <Select>
            <SelectTrigger className="h-9 min-w-[180px]">
              <span className="flex items-center">
                <ChevronDown className="mr-1 h-4 w-4" />
                Filters
              </span>
            </SelectTrigger>
            <SelectContent>
              {dynamicFilterableColumns.map((column) => (
                <SelectItem key={column.id} value={column.id}>
                  <div className="p-2">
                    <DataTableFacetedFilter
                      column={table.getColumn(column.id)}
                      title={column.title}
                      options={column.options}
                    />
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No categories found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex-1 text-sm text-muted-foreground">
          {data.total > 0 ? (
            <>
              Showing{" "}
              <strong>
                {pagination.pageIndex * pagination.pageSize + 1}-
                {Math.min(
                  (pagination.pageIndex + 1) * pagination.pageSize,
                  data.total
                )}
              </strong>{" "}
              of <strong>{data.total}</strong> categories
            </>
          ) : (
            "No categories found"
          )}
        </div>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${pagination.pageSize}`}
              onValueChange={(value) => {
                onPaginationChange({ pageIndex: 0, pageSize: Number(value) });
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue
                  placeholder={pagination.pageSize}
                  className="pr-1"
                >
                  {pagination.pageSize}
                </SelectValue>
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => onPaginationChange({ pageIndex: 0, pageSize: pagination.pageSize })}
              disabled={pagination.pageIndex === 0}
            >
              <span className="sr-only">Go to first page</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="m11 17-5-5 5-5" />
                <path d="m18 17-5-5 5-5" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => onPaginationChange({ pageIndex: pagination.pageIndex - 1, pageSize: pagination.pageSize })}
              disabled={pagination.pageIndex === 0}
            >
              <span className="sr-only">Go to previous page</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </Button>
            <div className="text-sm">
              Page{" "}
              <strong>
                {pagination.pageIndex + 1} of{" "}
                {pageCount || 1}
              </strong>
            </div>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => onPaginationChange({ pageIndex: pagination.pageIndex + 1, pageSize: pagination.pageSize })}
              disabled={pagination.pageIndex >= pageCount - 1}
            >
              <span className="sr-only">Go to next page</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => onPaginationChange({ pageIndex: pageCount - 1, pageSize: pagination.pageSize })}
              disabled={pagination.pageIndex >= pageCount - 1}
            >
              <span className="sr-only">Go to last page</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="m13 17 5-5-5-5" />
                <path d="m6 17 5-5-5-5" />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryDataTable; 